/**
 * OnboardingPage.tsx
 * ─────────────────────────────────────────────────────────────
 * 수포자 전용 온보딩 — 5단계 슬라이드
 *
 * 흐름:
 *   AuthPage "수포자입니다" 클릭
 *   → /onboarding (이 페이지, 5단계)
 *   → 완료 시 / (대시보드, 수포자 모드 ON)
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronRight, ChevronLeft, Heart, Zap, Star,
  TrendingUp, Shield, Clock, Trophy, Smile,
  CheckCircle, ArrowRight, Flame,
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

// ── 성공 스토리 데이터 ───────────────────────────────────────
const SUCCESS_STORIES = [
  {
    name: '박지수 (현재 고1)',
    avatar: '🧑‍🎓',
    before: '중2 때 수학 시험 12점. 포기 선언.',
    after: '이 앱으로 기울기 개념 3일 만에 완성. 기말고사 68점.',
    quote: '"틀려도 된다고 하니까 처음으로 수학이 무섭지 않았어요."',
    color: 'from-violet-500 to-purple-500',
  },
  {
    name: '김민준 (현재 중3)',
    avatar: '👦',
    before: '수학 선생님 얼굴만 봐도 긴장. 학원 3번 그만뒀음.',
    after: '5분짜리 개념 영상으로 y절편 완전 정복. 처음으로 스스로 문제 풀어봄.',
    quote: '"5분만 하면 된다고 해서 시작했는데 30분을 했어요 ㅋㅋ"',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    name: '이하은 (현재 고2)',
    avatar: '👧',
    before: '수포자라고 스스로 정의. 수학 공부 완전 포기 상태.',
    after: 'AI가 왜 틀렸는지 친절하게 알려줘서 오개념 3개 해결.',
    quote: '"수포자도 할 수 있구나 싶었어요. 이제 수포자 아닙니다."',
    color: 'from-emerald-500 to-teal-500',
  },
]

// ── 온보딩 슬라이드 5단계 정의 ───────────────────────────────
const SLIDES = [
  {
    id: 'empathy',
    emoji: '💔',
    bg: 'from-rose-900 via-pink-900 to-slate-900',
    accentColor: 'text-rose-300',
    badgeColor: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    badge: '당신 혼자가 아닙니다',
    title: '수학, 포기하고 싶었죠?',
    subtitle: '완전히 이해합니다. 솔직히 말할게요.',
    body: null,
    stats: [
      { value: '67%', label: '중학생이 수학에\n어려움을 느낍니다' },
      { value: '3번', label: '평균 포기 시도\n횟수입니다' },
      { value: '당신만\n아님', label: '그러니까\n괜찮습니다' },
    ],
    message: '수학을 못하는 게 아니에요.\n아직 맞는 방법을 못 찾은 것뿐입니다.',
    cta: '그래서 이 앱이 달라요 →',
  },
  {
    id: 'reframe',
    emoji: '🔄',
    bg: 'from-violet-900 via-purple-900 to-slate-900',
    accentColor: 'text-violet-300',
    badgeColor: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
    badge: '마인드셋 전환',
    title: '"수학 머리"는\n없습니다',
    subtitle: '과학적으로 증명된 사실이에요.',
    body: [
      { icon: '🧠', title: '수학 머리는 타고나지 않는다', desc: '뇌 과학 연구에 따르면 수학 능력은 연습으로 만들어집니다. 스탠포드대 교수 Jo Boaler의 연구 결과입니다.' },
      { icon: '⚡', title: '틀리는 게 오히려 도움된다', desc: '틀릴 때 뇌가 가장 많이 성장합니다. 오답이 학습의 핵심입니다.' },
      { icon: '📅', title: '하루 5분으로 충분하다', desc: '일관성이 양보다 중요합니다. 매일 5분이 일주일에 3시간보다 효과적입니다.' },
    ],
    message: null,
    cta: '내가 어디서 막혔는지 보기 →',
  },
  {
    id: 'stories',
    emoji: '🌟',
    bg: 'from-amber-900 via-orange-900 to-slate-900',
    accentColor: 'text-amber-300',
    badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    badge: '실제 이야기',
    title: '수포자였던\n그들의 변화',
    subtitle: '꾸며낸 이야기가 아닙니다.',
    body: null,
    message: null,
    cta: '나도 해볼 수 있을 것 같아요 →',
    stories: true,
  },
  {
    id: 'method',
    emoji: '🎯',
    bg: 'from-blue-900 via-indigo-900 to-slate-900',
    accentColor: 'text-blue-300',
    badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    badge: '이 앱의 방법',
    title: '수포자를 위한\n특별한 학습법',
    subtitle: '기존 학원·교과서와 완전히 다릅니다.',
    body: [
      { icon: '⏱️', title: '딱 5분 단위로 쪼개기', desc: '"오늘은 기울기가 뭔지만 알면 됩니다." 부담 없이 시작할 수 있어요.' },
      { icon: '💬', title: '틀려도 "잘했어" 피드백', desc: 'AI가 왜 그렇게 생각했는지 먼저 이해하고 부드럽게 교정합니다.' },
      { icon: '🎬', title: '5분 개념 영상 복습', desc: '문제가 틀리면 해당 개념만 집중 복습. 처음부터 다시 하지 않아도 됩니다.' },
      { icon: '📈', title: '나 vs 어제 나만 비교', desc: '다른 학생과 비교 없음. 어제보다 1% 성장하면 됩니다.' },
    ],
    message: null,
    cta: '딱 오늘 하루만 해볼게요 →',
  },
  {
    id: 'pledge',
    emoji: '🤝',
    bg: 'from-emerald-900 via-teal-900 to-slate-900',
    accentColor: 'text-emerald-300',
    badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    badge: '약속',
    title: '우리가 드리는\n약속 3가지',
    subtitle: '이 앱이 수포자에게 지키는 약속입니다.',
    promises: [
      { emoji: '🚫', text: '절대 비교하지 않겠습니다', sub: '점수·순위·다른 학생과의 비교 없음' },
      { emoji: '💪', text: '틀리는 걸 환영합니다', sub: '오답이 학습의 시작임을 알고 있습니다' },
      { emoji: '⏰', text: '딱 5분만 요구합니다', sub: '오늘 5분, 내일 5분. 그것으로 충분합니다' },
    ],
    message: null,
    cta: null,
    isFinal: true,
  },
]

// ── 진행 바 ─────────────────────────────────────────────────
function ProgressBar({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 rounded-full transition-all duration-500 flex-1 ${
            i < current ? 'bg-white' : i === current ? 'bg-white/70' : 'bg-white/20'
          }`}
        />
      ))}
    </div>
  )
}

// ── 스토리 카드 ──────────────────────────────────────────────
function StoryCard({ story, delay }: { story: typeof SUCCESS_STORIES[0]; delay: number }) {
  return (
    <div
      className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${story.color} flex items-center justify-center text-xl`}>
          {story.avatar}
        </div>
        <div>
          <p className="text-sm font-bold text-white">{story.name}</p>
          <div className="flex gap-1">
            {[1,2,3,4,5].map(s => <Star key={s} size={10} className="text-yellow-400 fill-yellow-400" />)}
          </div>
        </div>
      </div>
      <div className="space-y-2 mb-3">
        <div className="flex items-start gap-2">
          <span className="text-red-400 text-xs font-bold flex-shrink-0 mt-0.5">BEFORE</span>
          <p className="text-xs text-white/70 leading-relaxed">{story.before}</p>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-emerald-400 text-xs font-bold flex-shrink-0 mt-0.5">AFTER</span>
          <p className="text-xs text-white/80 leading-relaxed">{story.after}</p>
        </div>
      </div>
      <p className="text-xs text-amber-300 italic leading-relaxed">{story.quote}</p>
    </div>
  )
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────
export default function OnboardingPage() {
  const navigate = useNavigate()
  const { setSupojaMode, setSupojaOnboardingDone } = useProgressStore(s => ({
    setSupojaMode: s.setSupojaMode,
    setSupojaOnboardingDone: s.setSupojaOnboardingDone,
  }))

  const [current, setCurrent] = useState(0)
  const [animating, setAnimating] = useState(false)
  const [pledgeChecked, setPledgeChecked] = useState<boolean[]>([false, false, false])

  const slide = SLIDES[current]
  const isLast = current === SLIDES.length - 1
  const allPledged = pledgeChecked.every(Boolean)

  // 슬라이드 전환 애니메이션
  const goTo = (next: number) => {
    if (animating) return
    setAnimating(true)
    setTimeout(() => {
      setCurrent(next)
      setAnimating(false)
    }, 250)
  }

  const handleNext = () => {
    if (isLast) {
      handleFinish()
    } else {
      goTo(current + 1)
    }
  }

  const handleFinish = () => {
    setSupojaMode(true)
    setSupojaOnboardingDone()
    navigate('/', { replace: true })
  }

  const handleSkip = () => {
    // 온보딩 건너뛰어도 수포자 모드는 ON
    setSupojaMode(true)
    setSupojaOnboardingDone()
    navigate('/', { replace: true })
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${slide.bg} transition-all duration-700 flex flex-col`}>
      {/* 상단 네비 */}
      <div className="flex items-center justify-between p-5 pt-safe">
        <button
          onClick={() => current > 0 ? goTo(current - 1) : navigate('/auth')}
          className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex-1 px-4">
          <ProgressBar current={current} total={SLIDES.length} />
        </div>

        <button
          onClick={handleSkip}
          className="text-xs text-white/50 hover:text-white/80 transition-colors font-medium"
        >
          건너뛰기
        </button>
      </div>

      {/* 슬라이드 콘텐츠 */}
      <div
        className={`flex-1 flex flex-col px-6 pb-6 transition-all duration-250 ${
          animating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
        }`}
      >
        {/* 이모지 + 배지 */}
        <div className="flex items-center gap-3 mb-6 mt-2">
          <span className="text-5xl">{slide.emoji}</span>
          <span className={`text-xs font-bold px-3 py-1 rounded-full border ${slide.badgeColor}`}>
            {slide.badge}
          </span>
        </div>

        {/* 제목 */}
        <h1 className={`text-3xl font-black text-white leading-tight mb-2 whitespace-pre-line`}>
          {slide.title}
        </h1>
        <p className={`text-sm mb-6 leading-relaxed ${slide.accentColor}`}>
          {slide.subtitle}
        </p>

        {/* ── 슬라이드별 콘텐츠 ── */}

        {/* STEP 1: 공감 — 통계 */}
        {slide.stats && (
          <div className="space-y-4 flex-1">
            <div className="grid grid-cols-3 gap-3 mb-6">
              {slide.stats.map((s, i) => (
                <div key={i} className="bg-white/10 rounded-2xl p-3 text-center border border-white/10">
                  <div className="text-2xl font-black text-white whitespace-pre-line leading-tight mb-1">{s.value}</div>
                  <div className="text-xs text-white/60 whitespace-pre-line leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
              <p className={`text-sm font-bold leading-relaxed whitespace-pre-line ${slide.accentColor}`}>
                💬 {slide.message}
              </p>
            </div>
          </div>
        )}

        {/* STEP 2 & 4: 리스트 콘텐츠 */}
        {slide.body && (
          <div className="space-y-3 flex-1">
            {slide.body.map((item, i) => (
              <div key={i} className="bg-white/10 rounded-2xl p-4 flex gap-3 border border-white/10">
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-sm font-bold text-white mb-1">{item.title}</p>
                  <p className="text-xs text-white/70 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STEP 3: 성공 스토리 */}
        {slide.stories && (
          <div className="space-y-3 flex-1">
            {SUCCESS_STORIES.map((story, i) => (
              <StoryCard key={story.name} story={story} delay={i * 100} />
            ))}
          </div>
        )}

        {/* STEP 5: 약속 */}
        {(slide as any).promises && (
          <div className="flex-1">
            <div className="space-y-3 mb-6">
              {(slide as any).promises.map((p: { emoji: string; text: string; sub: string }, i: number) => (
                <button
                  key={i}
                  onClick={() => {
                    const next = [...pledgeChecked]
                    next[i] = !next[i]
                    setPledgeChecked(next)
                  }}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${
                    pledgeChecked[i]
                      ? 'border-emerald-400 bg-emerald-500/20'
                      : 'border-white/20 bg-white/10 hover:bg-white/15'
                  }`}
                >
                  <span className="text-2xl flex-shrink-0">{p.emoji}</span>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-white">{p.text}</p>
                    <p className="text-xs text-white/60 mt-0.5">{p.sub}</p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    pledgeChecked[i] ? 'border-emerald-400 bg-emerald-400' : 'border-white/30'
                  }`}>
                    {pledgeChecked[i] && <CheckCircle size={14} className="text-white" />}
                  </div>
                </button>
              ))}
            </div>

            {allPledged && (
              <div className="bg-emerald-500/20 border border-emerald-400/30 rounded-2xl p-4 text-center mb-2">
                <p className="text-emerald-300 font-bold text-sm">🎉 완벽합니다!</p>
                <p className="text-white/70 text-xs mt-1">이제 수포자 탈출 여정을 시작해요</p>
              </div>
            )}

            {!allPledged && (
              <p className="text-center text-xs text-white/40 mt-2">
                위 3가지를 확인해주세요 (탭해서 체크)
              </p>
            )}
          </div>
        )}

        {/* 하단 여백 */}
        <div className="h-4" />

        {/* ── CTA 버튼 ── */}
        {(slide as any).isFinal ? (
          <button
            onClick={handleFinish}
            disabled={!allPledged}
            className={`w-full py-4 rounded-2xl font-black text-base transition-all shadow-xl flex items-center justify-center gap-2 ${
              allPledged
                ? 'bg-gradient-to-r from-emerald-400 to-teal-400 text-white hover:opacity-90'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
          >
            <Flame size={18} />
            수포자 탈출 시작!
            <ArrowRight size={18} />
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="w-full py-4 rounded-2xl bg-white/20 backdrop-blur-sm text-white font-bold text-sm hover:bg-white/30 transition-all border border-white/20 flex items-center justify-center gap-2"
          >
            {slide.cta}
            <ChevronRight size={16} />
          </button>
        )}

        {/* 단계 표시 */}
        <p className="text-center text-xs text-white/30 mt-3">
          {current + 1} / {SLIDES.length}
        </p>
      </div>
    </div>
  )
}
